import { PermissionAccessLevel } from '@/api/generated/schemas/permissionAccessLevel';
import type { PermissionCatalogNode } from '@/api/generated/schemas/permissionCatalogNode';
import type { RolePermissionGrantItem } from '@/api/generated/schemas/rolePermissionGrantItem';

const LEVEL_RANK: Record<PermissionAccessLevel, number> = { None: 0, Read: 1, Edit: 2 };

export interface NodeLevelInfo {
  /** Explicitní grant uzlu (None se neukládá → null = bez grantu). */
  granted: PermissionAccessLevel | null;
  /** Efektivní úroveň po dědičnosti: max(grant, úroveň rodiče u dědících uzlů). */
  effective: PermissionAccessLevel;
  /** Klíč předka, ze kterého efektivní úroveň přišla (null = z vlastního grantu / žádná). */
  inheritedFrom: string | null;
}

/**
 * Náhled efektivních úrovní pro admin UI — replikuje backendový flatten
 * (`PermissionCatalog.Flatten`): union grantů s dědičností po stromu dolů,
 * uzly s `inheritsFromParent = false` grant rodiče nedostanou.
 */
export function computeEffectiveLevels(
  nodes: readonly PermissionCatalogNode[],
  grants: readonly RolePermissionGrantItem[]
): Map<string, NodeLevelInfo> {
  const nodesByKey = new Map(nodes.map((n) => [n.key, n]));
  const grantsByKey = new Map(
    grants.filter((g) => !g.isOrphan).map((g) => [g.permissionKey, g.level])
  );
  const result = new Map<string, NodeLevelInfo>();

  function resolve(key: string): NodeLevelInfo {
    const cached = result.get(key);
    if (cached) return cached;

    const node = nodesByKey.get(key);
    const granted = grantsByKey.get(key) ?? null;
    let effective = granted ?? PermissionAccessLevel.None;
    let inheritedFrom: string | null = null;

    if (node?.inheritsFromParent && node.parentKey) {
      const parent = resolve(node.parentKey);
      if (LEVEL_RANK[parent.effective] > LEVEL_RANK[effective]) {
        effective = parent.effective;
        inheritedFrom = parent.inheritedFrom ?? node.parentKey;
      }
    }

    const info: NodeLevelInfo = { granted, effective, inheritedFrom };
    result.set(key, info);
    return info;
  }

  for (const node of nodes) {
    resolve(node.key);
  }

  return result;
}
